import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users,
  Store,
  Package,
  ShoppingCart,
  Settings as SettingsIcon,
  MessageSquare,
  LogOut,
  ExternalLink,
  Menu,
  MoreHorizontal
} from "lucide-react";
import { useState } from "react";
import MobileDrawer from "@/components/MobileDrawer";
import ColorThemeSwitcher from "@/components/ColorThemeSwitcher";
import MobileTabBar from "@/components/MobileTabBar";

export default function AdminLayout({ children, title, subtitle }) {
  const { logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navigation = [
    { to: "/admin", label: "لوحة التحكم", icon: LayoutDashboard },
    { to: "/admin/users", label: "المستخدمون", icon: Users },
    { to: "/admin/shops", label: "المتاجر", icon: Store },
    { to: "/admin/products", label: "المنتجات", icon: Package },
    { to: "/admin/orders", label: "الطلبات", icon: ShoppingCart },
    { to: "/admin/whatsapp", label: "ربط واتساب", icon: MessageSquare },
    { to: "/admin/settings", label: "إعدادات النظام", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-muted/20 flex flex-col md:flex-row pb-safe" dir="rtl">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-64 bg-card border-l border-border flex-col justify-between shrink-0 shadow-sm">
        <div>
          <div className="p-6 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="font-display font-bold text-xl text-primary">لوحة الإدارة</h2>
              <p className="text-xs text-muted-foreground mt-0.5">إدارة بيب بيب</p>
            </div>
            <Link to="/app" target="_blank" title="عرض المتجر">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                <ExternalLink className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <nav className="p-4 space-y-1 overflow-x-auto md:overflow-visible flex md:flex-col gap-1 md:gap-0">
            {navigation.map((item) => {
              const active = location.pathname === item.to;
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    active
                      ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="p-4 border-t border-border mt-auto">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs uppercase">
              {user?.name?.[0] || "A"}
            </div>
            <div className="overflow-hidden text-right flex-1">
              <p className="text-xs font-semibold truncate">{user?.name || "الأدمن"}</p>
              <p className="text-[10px] text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full text-destructive hover:bg-destructive/10 border-border"
            onClick={() => {
              logout();
              window.location.href = "/";
            }}
          >
            <LogOut className="w-4 h-4 ml-2" />
            تسجيل الخروج
          </Button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header with Hamburger Menu */}
        <header className="safe-top-header md:hidden glass sticky top-0 z-30 border-b border-border px-4 h-14 bg-card/95 backdrop-blur-md flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display font-bold text-sm tracking-tight truncate">{title}</h1>
            <p className="text-[10px] text-muted-foreground truncate">إدارة بيب بيب</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <ColorThemeSwitcher variant="mobile" />
            <button onClick={() => setDrawerOpen(true)} aria-label="المزيد من أقسام الإدارة" className="grid h-10 w-10 place-items-center rounded-full text-muted-foreground active:bg-muted">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </header>

        <header className="hidden md:flex bg-card border-b border-border px-6 py-4 items-center justify-between shadow-sm">
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">{title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              النظام نشط
            </span>
          </div>
        </header>

        <div className="p-3 pb-6 md:p-8 flex-1 overflow-y-auto">
          {children}
        </div>
      </main>

      {/* Mobile Drawer */}
      <MobileDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        role="admin"
        extraNav={navigation}
      />
      <MobileTabBar
        items={[
          { id: "dashboard", label: "الرئيسية", icon: LayoutDashboard },
          { id: "shops", label: "المتاجر", icon: Store },
          { id: "orders", label: "الطلبات", icon: ShoppingCart },
          { id: "more", label: "المزيد", icon: MoreHorizontal },
        ]}
        activeId={location.pathname === "/admin" ? "dashboard" : location.pathname === "/admin/shops" ? "shops" : location.pathname === "/admin/orders" ? "orders" : "more"}
        onSelect={(id) => {
          const destinations = { dashboard: "/admin", shops: "/admin/shops", orders: "/admin/orders" };
          if (id === "more") setDrawerOpen(true);
          else navigate(destinations[id]);
        }}
        label="قائمة الإدارة"
      />
    </div>
  );
}
